/**
 * F5 · Her home screen is not your home screen.
 *
 * *"Same portal, different default ordering by role. Not a simplified version — a
 * differently ordered one."*
 *
 * The emphasis in that sentence is the whole specification. Every instinct here is
 * to build a "simple mode": fewer sections, softer words, the technical parts
 * removed. That is the failure `05-features.md` opens with — *the builder shows the
 * family his dashboard*, and its mirror image, the builder shows the family a
 * lesser dashboard. Both are the same mistake, which is deciding on someone's
 * behalf what they are interested in rather than what they look at first.
 *
 * So **both orderings contain every section**. Nothing is removed for anybody.
 * `03-shell-and-layout.md` says which way round: *"Yours leads with attention and
 * estate health; Mandri's leads with the week, the boys, what is expiring and what
 * is booked."* Christo is `owner` and Mandri is `partner` in Fabric's claims, so
 * the role is the ordering key and nothing new has to be stored to know who is who.
 *
 * ## Why "default" is load-bearing
 *
 * The spec says *default* ordering, and a default that cannot be changed is just an
 * ordering. Two reasons it has to be switchable, and the second is the real one:
 *
 *  · Somebody signing in as `display` or `guest` is not either of these people.
 *  · There is no way to check the other person's screen otherwise. A feature whose
 *    whole premise is "her page is not your page" that can only ever be seen from
 *    one side is a feature nobody can test, review or fix.
 *
 * The choice is stored on the device, not on the account. It is a way of looking at
 * a page, and pushing it to Fabric would mean a preference typed on a phone
 * silently rearranging the kitchen tablet.
 */
import type { Role } from "./auth";

/**
 * The sections of the glance page.
 *
 * `03-shell-and-layout.md`'s Pattern 3 sketches the order *greeting, figures,
 * WANTS YOU, sections* — and then says the order changes by role, with attention
 * named as one of the things an ordering can lead with. So the attention rail is
 * one of the movable sections here rather than a fixture above them; the sketch is
 * the owner's ordering drawn out, not a frame around it.
 *
 * The greeting, the date and the daily line are *not* in this list. They are the
 * page's identity rather than a section of it, and they stay at the top for
 * everybody.
 */
export type SectionId = "wants" | "week" | "boys" | "list" | "portals";

/**
 * Christo. Attention first, then the estate itself — the two things
 * `03-shell-and-layout.md` names — and the household sections under them.
 */
const OWNER: SectionId[] = ["wants", "portals", "week", "list", "boys"];

/**
 * Mandri. The week, the boys, and what is expiring and what is booked — which
 * arrive on the attention feed with dates on them and are drawn onto the week
 * itself, so the week section is two of her four leads rather than one.
 */
const PARTNER: SectionId[] = ["week", "boys", "list", "wants", "portals"];

export type Ordering = "owner" | "partner";

/** Everyone who is not the owner gets the household ordering, including a display. */
export function orderingFor(role: Role | null): Ordering {
  return role === "owner" ? "owner" : "partner";
}

/** What each ordering is called on screen. Names, not roles — this is a household. */
export const ORDERING_LABEL: Record<Ordering, string> = {
  owner: "Ordered for the operator",
  partner: "Ordered for the household",
};

const KEY = "steyn.frontdoor.order.v1";

class OrderPref {
  /** Null means "follow the role", which is what `default` in the spec means. */
  chosen = $state<Ordering | null>(null);

  constructor() {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw === "owner" || raw === "partner") this.chosen = raw;
    } catch {
      // Storage refused. The role default still applies; nothing is broken and
      // there is nothing useful to tell anyone about it.
    }
  }

  /**
   * The sections, in order.
   *
   * F11 folds in here rather than in the page: on a Sunday the week leads whoever
   * is reading, because *"the tablet shows the week ahead rather than today's
   * tasks"* is a statement about what Sunday is for, and it is no less true on a
   * phone. Everything else keeps its place, so the page is recognisably the same
   * page with one section hoisted rather than a different layout once a week.
   */
  for(role: Role | null, sunday = false): SectionId[] {
    const base = (this.chosen ?? orderingFor(role)) === "owner" ? OWNER : PARTNER;
    if (!sunday) return base;
    return ["week", ...base.filter((s) => s !== "week")];
  }

  /** Which ordering is actually in force, whether chosen or inherited from the role. */
  active(role: Role | null): Ordering {
    return this.chosen ?? orderingFor(role);
  }

  set(o: Ordering | null): void {
    this.chosen = o;
    try {
      if (o === null) localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, o);
    } catch {
      // Same as above: the choice holds for this session and is not worth a message.
    }
  }
}

export const order = new OrderPref();
